import React, { useContext, useState } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import { get, groupBy, map, isEqual, pick, range } from 'lodash';
import moment from 'moment-timezone';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { useHistory } from 'react-router-dom';
import { History } from "history";
import { TTypedTFunction } from '@lib/useTypedTranslation';

import { AmChart, coreTheme } from '../../../../../../components/chart/amChart';
import { IBatteryLevel, ITzBatteryLevel } from '../../../../../../services/core/devicePerformance';
import { DeviceEvent, DeviceEventName, TzDeviceEvent } from '../../../../../../services/core/eventsTypes';
import { NoSelectionOverlay } from '../../../../../../components/card/noSelectionOverlay';
import { EditEvents, EditEventsAction, useEditEventsDispatch } from '../events/edit-events';
import { deviceEventFilterGroupNames, IDeviceEventFilterState } from '../events/deviceEventsTypes';
import { getDeviceEventFilterGroups } from '../events/deviceEvents';
import { addEventOverlaysToLineChart } from './lib/eventOverlays';
import { EventsShown } from '../events-shown';
import { DeviceInfoContext } from '../../../index';
import { chartNames, ChartName } from './lib/chartNames';
import { encode } from '../../../../../../lib/base64';
import { ResetZoomButton } from './lib/resetZoomButton';
import { adjustChartAndLegendRatio } from './adjustChartAndLegendRatio';
import { useBrandContext } from '../../../../../../context/brand';
import { useFeatureTogglesContext } from '../../../../../../context/featureToggles';
import { useCurrentWorldContext } from '../../../../../../context/currentWorld';

import './resetZoomButton.css';
import './charts.css';
import '../events/deviceEvents.css';
import { Feature } from '../../../../../app/productsWrapper';
import { useWorldRoutes } from '../../../../../../routes/parts/allWorldRoutes';
import { formatDateAxisLabelsInTimeZone } from '../../../../../../components/chart/lib';

interface ICommonChargeLevelProps {
  dateRange?: { from: number, to: number },
  classname: string,
  eventFilters: IDeviceEventFilterState,
  dispatchEventFilters: React.Dispatch<EditEventsAction<ChartName>>,
  visible: boolean,
  onMount: (chart: am4charts.XYChart) => am4charts.XYChart,
  /** Overrides the chart's default "Device Charge Level" header - e.g. with a date suffix,
   * matching the batteries views' "... for {{date}}" convention (see DEVICE_CHARGE_LEVEL_FOR). */
  title?: string,
  /** Optional x-axis title (e.g. "Device Local Time (Central Africa Time)" - see
   * selectedDateChart.tsx's DEVICE_LOCAL_TIME usage). Left unset by every performance-tab usage of
   * this component, which has never labelled the axis - only set by callers (e.g. the low power
   * anomaly examination page) that need to make the axis's time zone explicit. */
  xAxisTitle?: string,
  /** Hides the footer's "Apply All" button. Defaults to true (every performance-tab usage of
   * this component shares a DeviceChartEventsFilterState across multiple charts, so applying
   * "all" is meaningful there). Pass false where dispatchEventFilters instead comes from a
   * single-chart, session-only reducer (see useAnomalyExaminationEventFilters) - there
   * onApplyAll behaves identically to onApply, so the button would offer nothing extra. */
  showApplyAll?: boolean,
  /** Events rendered as checked and disabled in the Edit Events dialog, and excluded from
   * DISPLAY_ALL's toggle - see EditEvents' own lockedEvents prop. Defaults to none, matching
   * every performance-tab usage of this component; only the low power anomaly examination page
   * locks its own primary event (see lowPowerSection.tsx). */
  lockedEvents?: readonly DeviceEventName[]
}

export type ChargeLevelProps = ICommonChargeLevelProps & (
  { displayTimeZone?: undefined, data: IBatteryLevel[], events?: DeviceEvent[] } |
  /** Zoned display requires API results that include UTC instants. */
  { displayTimeZone: string, data: ITzBatteryLevel[], events?: TzDeviceEvent[] }
);

export const dependencies = {
  createSeries
};

// istanbul ignore next
function createSeries(name: string, lineColour: string, index: number, chart: any, classname: string, t: TTypedTFunction, displayTimeZone?: string) {
  const colour = am4core.color(lineColour);

  const series = chart.series.push(new am4charts.LineSeries());
  series.data = chart.data[index];
  series.dataFields.dateX = 'local';
  series.dataFields.valueY = 'level';
  series.name = name;
  series.connect = false;
  series.autoGapCount = 600;
  series.stroke = colour;
  series.strokeWidth = 2;
  series.fill = colour;
  series.fillOpacity = 0.2;
  series.tooltipHTML = '';

  series.adapter.add('tooltipHTML', (_: any, target: any) => {
    const tooltip = target.tooltipDataItem as any;
    const date = formatDate(tooltip.dateX, displayTimeZone);
    return `<div class="tooltip-container_device-charge-level"><span class="device-charge-level-${classname}-tooltip-title">${date}</span>
    </br><span class="device-charge-level-${classname}-tooltip-text"><div class="key" style="border-color: ${lineColour}"></div>${t('CHARGE_LEVEL')} {valueY}</span>
    </br><span class="device-charge-level-${classname}-tooltip-text serialnumber">${t('BATTERY_SN')}: ${name}</span></div>`;
  });

  series.dummyData = { colour };

  return series;
}


export function redirectToBatteryEssentialsLink(batteryId: string, history: History, baseBatteryPath: string) {
  const base64Id = encode(batteryId);
  history.push(`${baseBatteryPath}?id=${encodeURIComponent(base64Id)}&ts=${moment().valueOf()}`);
}

export function formatDate(date: number, displayTimeZone?: string) {
  const local = moment.utc(date).tz(displayTimeZone || 'UTC');
  return `${local.format('LLLL')} ${local.format('LT')}`;
}

export function DeviceChargeLevel(props: ChargeLevelProps) {
  const { classname, eventFilters, dispatchEventFilters, onMount, title: titleOverride, showApplyAll = true, lockedEvents = [] } = props;
  const featureToggles = useFeatureTogglesContext();
  const { features } = useCurrentWorldContext();
  const { isElemez } = useBrandContext();
  const { platformType, timeZoneOffset } = useContext(DeviceInfoContext);
  const { t } = useTranslation(['performance', 'editEvents', 'translation']);
  const [chartDataLoading, setChartDataLoading] = useState(false);
  const [chart, setChart] = useState(undefined);
  const baseBatteryPath = useWorldRoutes().batteryEssentials.battery;
  const [chartReady, setChartReady] = useState(false);

  /* this state is needed for the button outside of amcharts,
   as the chart instance is not up to date outside of amcharts functions */
  const [zoomedIn, setZoomedIn] = useState(false);

  const history = useHistory();

  const {
    handleUpdate, handleApply, handleApplyAll, handleCancel
  } = useEditEventsDispatch(
    dispatchEventFilters, chartNames.deviceChargeLevel, setLoadingTrue
  );

  const title = titleOverride || t('DEVICE_CHARGE_LEVEL', { ns: 'performance' });

  function setLoadingTrue() {
    setChartReady(false);
    setChartDataLoading(true);
  }

  function onReady() {
    if (!chartDataLoading) {
      setChartReady(true);
    }
  }

  const chartClassname = `core-device_performance-${classname}-device-charge-level ${chartReady ? 'chart-ready' : ''}`;

  return (
    <div className={chartClassname}>
      <div className="chart-header">
        <div className="chart-header-left">
          <NoSelectionOverlay noSelectionText={t('NO_DATA_AVAILABLE', { ns: "translation" })} show={props.data.length === 0} />
          <div className="chart-title">{title}</div>
          <ResetZoomButton zoomedIn={zoomedIn} resetZoom={resetZoom} />
        </div>
        <div className="chart-edit-events">
          <EventsShown eventFilters={eventFilters.appliedEventFilters} />
          <EditEvents
            subheader={`‘${title}’`}
            eventGroupNames={deviceEventFilterGroupNames}
            eventFilterGroups={getDeviceEventFilterGroups(platformType, featureToggles)}
            eventFilterState={eventFilters.currentEventFilters}
            lockedEvents={lockedEvents}
            handleUpdate={handleUpdate}
            handleApply={handleApply}
            handleApplyAll={showApplyAll ? handleApplyAll : undefined}
            handleCancel={handleCancel}
          />
        </div>
      </div>
      <div className={`chart-and-legend-container`}>
        <div className="chart-container">
          <AmChart
            tag={`${classname}-device-level-chart`}
            chartProvider={createChart}
            onMount={(chart, ...rest) => { onMount(chart); onDataUpdated(chart, ...rest); return chart; } }
            onUpdate={onDataUpdated}
            dataProvider={() => { setChartDataLoading(false); return getData(); }}
            link={pick(props, 'data', 'events', 'visible', 'displayTimeZone')}
            onDataValidated={onReady}
            cypressId="last24HoursOrSelectedDateChargeLevel"
          />
        </div>
      </div>
    </div>
  );

  function resetZoom() {
    setZoomedIn(false);
    const xAxis: am4charts.DateAxis = chart.xAxes.values[0];
    xAxis.zoomToDates(new Date(xAxis.min), new Date(xAxis.max));
  }

  // Mirrors useChartZoom's start/end check (components/chart/useChartZoom.ts) rather than
  // comparing the zoomed range's duration against a fixed 24-hour threshold: a full, un-zoomed
  // "selected date" axis already spans just under 24 hours (endOf('day') - startOf('day') =
  // 86399999ms), and a full "24-hour" axis spans exactly 23 hours, so the old duration check was
  // already true at the default range - marking the chart as zoomed (and enabling Reset Zoom) as
  // soon as it mounted, before the user ever actually zoomed in.
  function handleZoom(xAxis: am4charts.DateAxis) {
    setZoomedIn(xAxis.start > 0.001 || xAxis.end < 0.999);
  }

  // Applies the intended window from the current props - dateRange/timeZoneOffset are already
  // known regardless of whether the fetch for that window has resolved yet. Called both from
  // createChart (mount) and onDataUpdated (every subsequent update): createChart only ever runs
  // once, but selectedDate (hence dateRange) changes on every day switch, so re-applying it here
  // too keeps the axis in sync - otherwise it stayed pinned to whichever day/window was current
  // at mount, so a newly selected day's data could fall entirely outside the displayed range.
  function setAxisBounds(xAxis: am4charts.DateAxis) {
    if (props.classname === '24-hour') {
      xAxis.max = moment.utc().add(timeZoneOffset, 'minutes').endOf('hour').valueOf();
      xAxis.min = moment.utc(xAxis.max).startOf('hour').subtract(23, 'hour').valueOf();
    } else {
      xAxis.max = props.dateRange.to;
      xAxis.min = props.dateRange.from;
    }
  }

  function createChart(id: string) {
    am4core.useTheme(coreTheme);

    const chart: am4charts.XYChart = am4core.create(id, am4charts.XYChart);

    chart.height = am4core.percent(85);
    chart.paddingTop = 42;

    const xAxis = new am4charts.DateAxis();
    chart.xAxes.push(xAxis);
    xAxis.minZoomCount = 120;
    xAxis.events.on('startendchanged', () => handleZoom(xAxis));
    xAxis.dataFields.data = "local";
    xAxis.renderer.minGridDistance = 50;
    xAxis.renderer.grid.template.strokeWidth = 0;
    setAxisBounds(xAxis);

    xAxis.groupData = false;
    xAxis.cursorTooltipEnabled = false;

    xAxis.dateFormatter.utc = true;
    formatDateAxisLabelsInTimeZone(xAxis, props.displayTimeZone);
    xAxis.dateFormats.setKey("day", "dt");
    xAxis.markUnitChange = false;

    xAxis.renderer.labels.template.disabled = false;
    xAxis.renderer.labels.template.location = 0.001 as any;
    if (props.xAxisTitle) {
      // amCharts reserves the title's own line height out of the chart's existing (fixed, 14rem)
      // container rather than growing to fit it, which otherwise shrinks the plot/grid - see the
      // low power anomaly examination page's .chart-container height rule in charts.css, which
      // gives its chart-container back the extra height this costs so its grid (and the event
      // marker ticks/labels sized for it in eventOverlays.ts) end up the same size as every other
      // DeviceChargeLevel instance that leaves xAxisTitle unset.
      xAxis.title.text = props.xAxisTitle;
    }
    xAxis.baseInterval = { timeUnit: "second", count: 1 };
    xAxis.gridIntervals.setAll([
      { timeUnit: "minute", count: 1 },
      { timeUnit: "minute", count: 5 },
      { timeUnit: "minute", count: 10 },
      { timeUnit: "minute", count: 30 },
      { timeUnit: "hour", count: 1 },
      { timeUnit: "hour", count: 3 },
      { timeUnit: "hour", count: 6 },
      { timeUnit: "hour", count: 12 }
    ]);

    const yAxis = new am4charts.ValueAxis();
    chart.yAxes.push(yAxis);
    yAxis.min = 0;
    yAxis.max = 100;
    yAxis.renderer.minGridDistance = 25;
    yAxis.numberFormatter.numberFormat = "#'%'";
    yAxis.cursorTooltipEnabled = false;
    yAxis.contentAlign = 'right';

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.disabled = true;
    chart.cursor.lineX.disabled = false;
    chart.cursor.maxTooltipDistance = 0;

    //grid with 25 step increment
    function createGrid(value: number) {
      const range = yAxis.axisRanges.create();
      range.value = value;
      range.label.text = "{value}";
    }
    yAxis.renderer.grid.template.disabled = true;
    yAxis.renderer.labels.template.disabled = true;
    createGrid(0);
    createGrid(25);
    createGrid(50);
    createGrid(75);
    createGrid(100);

    chart.zoomOutButton.disabled = true;

    chart.legend = new am4charts.Legend();
    chart.legend.useDefaultMarker = true;
    chart.legend.labels.template.wrap = false;
    chart.legend.labels.template.fontWeight = '400';
    chart.legend.labels.template.dy = -8;
    chart.legend.labels.template.maxWidth = 150;
    chart.legend.labels.template.truncate = false;
    chart.legend.maxColumns = 4;
    chart.legend.height = am4core.percent(15);

    chart.legend.itemContainers.template.togglable = false;
    chart.legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;

    chart.events.on("maxsizechanged", function (ev) {
      adjustChartAndLegendRatio(chart, 85, 75);
    });

    const batteryEssentialsDevicesFeature = isElemez ? Feature.batteryEssentialsDevices : Feature.panasonicSmartBatteryMonitorDevices;
    const legendIsClickable = features.includes(batteryEssentialsDevicesFeature);
    chart.legend.itemContainers.template.events.on("over", (event) => {
      event.target.cursorOverStyle = am4core.MouseCursorStyle.default;
      if (((event.target.dataItem.dataContext as any).data[0].smartBattery?.id) && legendIsClickable) {
        event.target.cursorOverStyle = am4core.MouseCursorStyle.pointer;
      }
    });

    if (legendIsClickable) {
      chart.legend.itemContainers.template.events.on("hit", function (ev) {
        const smartBatteryId = (ev.target.dataItem.dataContext as any).data[0].smartBattery?.id;
        if (smartBatteryId) {
          redirectToBatteryEssentialsLink(smartBatteryId, history, baseBatteryPath);
        }
      });
    }

    chart.legend.labels.template.adapter.add("stroke", function (_, target) {
      if (((target.parent.dataItem.dataContext as any).data[0].smartBattery?.id) && legendIsClickable) {
        return target.stroke = am4core.color('#07c');
      }
    });

    const markerTemplate = chart.legend.markers.template;
    markerTemplate.width = 13;
    markerTemplate.height = 30;
    markerTemplate.strokeWidth = 2;
    markerTemplate.dx = 1;

    markerTemplate.disposeChildren();
    const legendItem = markerTemplate.createChild(am4core.Rectangle);
    legendItem.fillOpacity = 0.2;
    legendItem.width = 13;
    legendItem.height = 13;

    // istanbul ignore next
    legendItem.adapter.add("stroke", function (_, target) {
      if (target.dataItem && target.dataItem.dataContext && (target.dataItem.dataContext as any).dummyData) {
        return (target.dataItem.dataContext as any).dummyData.colour;
      }
    });

    // istanbul ignore next
    legendItem.adapter.add("fill", function (_, target) {
      if (target.dataItem && target.dataItem.dataContext && (target.dataItem.dataContext as any).dummyData) {
        return (target.dataItem.dataContext as any).dummyData.colour;
      }
    });

    // allow access to chart outside of amchart functions
    setChart(chart);

    return chart;
  }

  function onDataUpdated(chart: am4charts.XYChart, chartData: any[], prevProps?: any, currentProps?: any) {
    setAxisBounds(chart.xAxes.values[0] as am4charts.DateAxis);

    // remove old series if any exist
    if (chart.series && chart.series.length > 0) {
      chart.series.clear();
    }

    chartData.forEach((battery: any, index: number) => {
      const lineColours = ['#5ECAEA', '#709BDF', '#8076E0', '#3BB09A', '#DF7097', '#EA985E', '#5FB952'];
      if (props.displayTimeZone) {
        dependencies.createSeries(battery[0].smartBattery?.serialNumber || '-', lineColours[index], index, chart, classname, t, props.displayTimeZone);
      } else {
        dependencies.createSeries(battery[0].smartBattery?.serialNumber || '-', lineColours[index], index, chart, classname, t);
      }
    });

    const currentChartData = currentProps.link.data;
    const currentEvents = currentProps.link.events;

    const eventDataHasChanged = !isEqual(currentEvents, get(prevProps, 'link.events'));
    const chartDataHasChanged = !isEqual(currentChartData, get(prevProps, 'link.data'));

    // add overlays on mount / on update if events have changed and chart data exists
    const shouldAddOrUpdateEventOverlays = (eventDataHasChanged || chartDataHasChanged) && currentChartData.length !== 0;

    if (shouldAddOrUpdateEventOverlays) {
      if (props.displayTimeZone) {
        addEventOverlaysToLineChart(chart, chart.xAxes.values[0] as am4charts.DateAxis, currentProps.link.events, t, {
          displayTimeZone: props.displayTimeZone
        });
      } else {
        addEventOverlaysToLineChart(chart, chart.xAxes.values[0] as am4charts.DateAxis, currentProps.link.events, t);
      }
    }

    // If the chart has just become visible - e.g. LowPowerSection's hideChildren clearing the
    // [hidden] attribute once loading finishes, or the performance tab's tab-switching (both keep
    // the chart mounted while hidden rather than unmounting it) - force a full redraw. amCharts
    // can compute its layout (axis labels, event overlays, legend, etc.) against the hidden
    // container's size (zero, for display: none) and won't always self-correct once it becomes
    // visible again. prevProps is only undefined on a genuine first mount, which never needs this
    // (there's no earlier, possibly-wrong layout to correct).
    if (prevProps && get(currentProps, 'link.visible') && !get(prevProps, 'link.visible')) {
      chart.deepInvalidate();
    }

    return chart;
  }

  function getData(): Array<any> {
    if (props.data.length > 0) {
      const dataGroupedByBattery = groupBy(props.data, 'smartBattery.serialNumber');
      const dataSortedByDate = map(dataGroupedByBattery, (batteryLevels: IBatteryLevel[]) => {
        const formattedData = batteryLevels.map(batteryData => {
          return {
            ...batteryData,
            local: props.displayTimeZone ? Number((batteryData as ITzBatteryLevel).utc) : parseInt(batteryData.local)
          };
        });
        return sortByDate(formattedData);
      });
      return dataSortedByDate;
    }
    return [];
  }

  function sortByDate(data: Array<any>): Array<any> {
    return data.sort((a: any, b: any) => a.local - b.local);
  }
}
